import styled from 'styled-components';

// COLORS used throughout the dashboard - Updated Blue-Gray Theme
export const COLORS = {
    // Primary palette - refined blue-purple tones
    primary: '#6366f1',        // Indigo primary
    primaryLight: '#818cf8',   // Lighter indigo
    primaryDark: '#4f46e5',    // Darker indigo
    secondary: '#3b82f6',      // Blue
    accent: '#8b5cf6',         // Purple accent
    
    // Status colors
    success: '#10b981',        // Emerald green
    successLight: '#34d399',
    warning: '#f59e0b',        // Amber
    warningLight: '#fbbf24',
    danger: '#ef4444',         // Red
    dangerLight: '#f87171',
    info: '#06b6d4',           // Cyan
    
    // Background colors - Blue-gray theme
    bgPrimary: '#0f1419',      // Darkest background
    bgSecondary: '#1a1f2e',    // Card/panel background
    bgTertiary: 'rgba(30, 35, 50, 0.6)',  // Elevated surfaces
    bgCard: 'rgba(40, 45, 65, 0.5)',      // Card backgrounds
    bgHover: 'rgba(99, 102, 241, 0.1)',   // Hover states
    
    // Legacy support
    dark: '#0f1419',
    
    // Text colors
    light: '#e8eaed',
    lightSecondary: 'rgba(255, 255, 255, 0.85)',
    muted: 'rgba(255, 255, 255, 0.5)',
    
    // Border colors
    border: 'rgba(100, 150, 200, 0.15)',
    borderLight: 'rgba(100, 150, 200, 0.25)',
    borderAccent: 'rgba(99, 102, 241, 0.3)',
    
    // Chart colors - vibrant but cohesive
    chart: ['#6366f1', '#3b82f6', '#8b5cf6', '#06b6d4', '#10b981', '#f59e0b', '#ec4899']
};

export const DashboardWrapper = styled.div`
    background: linear-gradient(180deg, ${COLORS.bgPrimary} 0%, ${COLORS.bgSecondary} 100%);
    height: 100vh;
    width: 100%;
    overflow: auto;
    padding: 14px;
    box-sizing: border-box;
    
    /* Custom scrollbar styling - matches workspace theme */
    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: rgba(0, 0, 0, 0.2);
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: rgba(100, 150, 200, 0.3);
        border-radius: 4px;
        
        &:hover {
            background: rgba(100, 150, 200, 0.5);
        }
    }

    /* Firefox scrollbar */
    scrollbar-width: thin;
    scrollbar-color: rgba(100, 150, 200, 0.3) rgba(0, 0, 0, 0.2);
    
    @media screen and (min-width: 2560px) {
        padding: 18px;
    }
    
    @media screen and (max-width: 1920px) {
        padding: 12px;
    }
    
    @media screen and (max-width: 1366px) {
        padding: 8px;
    }
    
    @media screen and (max-width: 768px) {
        padding: 6px;
    }
`;

export const InfoPanel = styled.div`
    padding: 20px;
    background: ${COLORS.bgTertiary};
    border: 1px solid ${COLORS.border};
    border-radius: 12px;
    margin-bottom: 20px;
    color: ${COLORS.light};
    backdrop-filter: blur(10px);
    
    h2 {
        color: ${COLORS.primary};
        margin-top: 0;
    }
    
    ul {
        list-style-type: none;
        padding-left: 0;
        
        li {
            padding: 6px 0;
            border-bottom: 1px dashed ${COLORS.border};
            
            &:last-child {
                border-bottom: none;
            }
        }
    }
    
    .file-name {
        color: ${COLORS.primaryLight};
        font-family: monospace;
        padding: 2px 5px;
        background-color: rgba(99, 102, 241, 0.15);
        border-radius: 3px;
        margin-right: 8px;
    }
    
    .file-description {
        color: ${COLORS.muted};
    }
`;

export const PanelContent = styled.div`
    padding: 14px;
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    background: transparent;
    
    @media screen and (max-width: 1920px) {
        padding: 12px;
    }
    
    @media screen and (max-width: 1366px) {
        padding: 10px;
    }
`;

export const PanelTitle = styled.h3`
    color: ${COLORS.light};
    font-size: 18px;
    margin-top: 0;
    margin-bottom: 14px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 8px;
    
    svg {
        color: ${COLORS.primary};
    }
    
    @media screen and (max-width: 1920px) {
        font-size: 16px;
        margin-bottom: 10px;
    }
    
    @media screen and (max-width: 1366px) {
        font-size: 14px;
        margin-bottom: 8px;
    }
`;

export const StatGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(${props => props.$cols || 2}, 1fr);
    gap: 10px;
    margin-bottom: 14px;
    
    @media screen and (max-width: 1920px) {
        gap: 8px;
        margin-bottom: 12px;
    }
    
    @media screen and (max-width: 1366px) {
        gap: 6px;
        margin-bottom: 10px;
    }
`;

export const StatCard = styled.div`
    background: rgba(25, 30, 45, 0.8);
    border: 1px solid ${COLORS.border};
    border-radius: 10px;
    padding: 14px;
    display: flex;
    flex-direction: column;
    transition: all 0.2s ease;
    backdrop-filter: blur(5px);
    
    &:hover {
        background: rgba(99, 102, 241, 0.15);
        border-color: ${COLORS.borderAccent};
    }
    
    .stat-title {
        color: ${COLORS.muted};
        font-size: 12px;
        font-weight: 500;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        margin-bottom: 8px;
    }
    
    .stat-value {
        color: ${COLORS.light};
        font-size: ${props => props.$large ? '28px' : '20px'};
        font-weight: 600;
    }
    
    .stat-footer {
        color: ${props => props.$positive ? COLORS.success : props.$negative ? COLORS.danger : COLORS.muted};
        font-size: 12px;
        margin-top: 8px;
        display: flex;
        align-items: center;
        gap: 4px;
    }
    
    @media screen and (max-width: 1920px) {
        padding: 12px;
        
        .stat-title {
            font-size: 11px;
            margin-bottom: 6px;
        }
        
        .stat-value {
            font-size: ${props => props.$large ? '24px' : '18px'};
        }
        
        .stat-footer {
            font-size: 11px;
            margin-top: 6px;
        }
    }
    
    @media screen and (max-width: 1366px) {
        padding: 10px;
        
        .stat-title {
            font-size: 10px;
            margin-bottom: 4px;
        }
        
        .stat-value {
            font-size: ${props => props.$large ? '20px' : '16px'};
        }
        
        .stat-footer {
            font-size: 10px;
            margin-top: 4px;
        }
    }
`;

export const ChartContainer = styled.div`
    height: ${props => props.$height || '180px'};
    width: 100%;
    margin-top: ${props => props.$marginTop || '0'};
    position: relative;
    background: transparent;
    
    @media screen and (max-width: 1920px) {
        height: ${props => 
            props.$height === '180px' ? '150px' :
            props.$height === '160px' ? '140px' :
            props.$height === '140px' ? '130px' :
            props.$height === '130px' ? '120px' :
            props.$height
        };
    }
    
    @media screen and (max-width: 1366px) {
        height: ${props => 
            props.$height === '180px' ? '130px' :
            props.$height === '160px' ? '125px' :
            props.$height === '140px' ? '115px' :
            props.$height === '130px' ? '110px' :
            props.$height
        };
    }
`;

export const TopicTag = styled.div`
    background: linear-gradient(135deg, rgba(99, 102, 241, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%);
    border: 1px solid ${COLORS.borderAccent};
    color: ${COLORS.primaryLight};
    padding: 4px 10px;
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
    display: inline-block;
    margin-right: 6px;
    margin-bottom: 6px;
    
    @media screen and (max-width: 1920px) {
        padding: 3px 8px;
        font-size: 9px;
        margin-right: 4px;
        margin-bottom: 4px;
    }
    
    @media screen and (max-width: 1366px) {
        padding: 2px 6px;
        font-size: 8px;
        margin-right: 3px;
        margin-bottom: 3px;
    }
`;

export const NotificationList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    overflow-y: auto;
    flex-grow: 1;
    padding-bottom: 5px;
    
    @media screen and (max-width: 1920px) {
        gap: 8px;
        padding-bottom: 4px;
    }
    
    @media screen and (max-width: 1366px) {
        gap: 6px;
        padding-bottom: 3px;
    }
`;

export const NotificationItem = styled.div`
    background: ${COLORS.bgCard};
    border: 1px solid ${COLORS.border};
    border-radius: 10px;
    padding: 12px;
    display: flex;
    gap: 12px;
    transition: all 0.2s ease;
    
    &:hover {
        background: ${COLORS.bgHover};
        border-color: ${COLORS.borderAccent};
    }
    
    .icon-container {
        width: 36px;
        height: 36px;
        border-radius: 10px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        background: ${props => 
            props.$type === 'feature' ? 'rgba(99, 102, 241, 0.15)' : 
            props.$type === 'tip' ? 'rgba(16, 185, 129, 0.15)' : 
            props.$type === 'update' ? 'rgba(245, 158, 11, 0.15)' : 
            'rgba(6, 182, 212, 0.15)'
        };
        border: 1px solid ${props => 
            props.$type === 'feature' ? 'rgba(99, 102, 241, 0.3)' : 
            props.$type === 'tip' ? 'rgba(16, 185, 129, 0.3)' : 
            props.$type === 'update' ? 'rgba(245, 158, 11, 0.3)' : 
            'rgba(6, 182, 212, 0.3)'
        };
        color: ${props => 
            props.$type === 'feature' ? COLORS.primary : 
            props.$type === 'tip' ? COLORS.success : 
            props.$type === 'update' ? COLORS.warning : 
            COLORS.info
        };
    }
    
    .content {
        flex-grow: 1;
    }

    .title {
        color: ${COLORS.light};
        font-weight: 600;
        margin-bottom: 4px;
        font-size: 13px;
    }

    .description {
        color: ${COLORS.lightSecondary};
        font-size: 11px;
        margin-bottom: 6px;
        line-height: 1.4;
    }
    
    .date {
        color: ${COLORS.muted};
        font-size: 10px;
    }
    
    @media screen and (max-width: 1920px) {
        padding: 10px;
        gap: 10px;
        margin-bottom: 5px;
        
        .icon-container {
            width: 30px;
            height: 30px;
            border-radius: 8px;
            
            svg {
                font-size: 12px;
            }
        }
        
        .title {
            font-size: 11px;
            margin-bottom: 2px;
        }
        
        .description {
            font-size: 9px;
            margin-bottom: 4px;
        }
        
        .date {
            font-size: 8px;
        }
    }
    
    @media screen and (max-width: 1366px) {
        padding: 8px;
        gap: 8px;
        
        .icon-container {
            width: 26px;
            height: 26px;
            border-radius: 6px;
            
            svg {
                font-size: 11px;
            }
        }
        
        .title {
            font-size: 10px;
            margin-bottom: 2px;
        }
        
        .description {
            font-size: 8px;
            margin-bottom: 3px;
        }
        
        .date {
            font-size: 7px;
        }
    }
`;

export const ProgressCircle = styled.div`
    width: ${props => props.$size || '80px'};
    height: ${props => props.$size || '80px'};
`;

// Common data structures for the dashboard components
export const mockAdviceStatusData = {
    totalAdvices: 124,
    inProgress: 18,
    completed: 98,
    cancelled: 8,
    avgCompletionTime: "3.2 days",
    popularTopics: [
        { name: 'Career Development', value: 35 },
        { name: 'Personal Finance', value: 25 },
        { name: 'Business Strategy', value: 20 },
        { name: 'Leadership', value: 15 },
        { name: 'Work-Life Balance', value: 5 }
    ]
};

export const mockEarningsData = {
    thisMonth: 2450,
    allTime: 18750,
    pendingPayments: 750,
    nextPayout: "June 15, 2024",
    monthlyComparison: 18, // percentage increase from previous month
    monthlyEarnings: [
        { name: 'Jan', amount: 1800 },
        { name: 'Feb', amount: 1950 },
        { name: 'Mar', amount: 1700 },
        { name: 'Apr', amount: 2100 },
        { name: 'May', amount: 2450 }
    ]
};

export const mockEngagementData = {
    profileViews: 342,
    newInquiries: 28,
    externalClicks: 65,
    conversionRate: 8.2, // percentage
    engagementTrend: [
        { name: 'Week 1', views: 75, inquiries: 4 },
        { name: 'Week 2', views: 85, inquiries: 7 },
        { name: 'Week 3', views: 90, inquiries: 8 },
        { name: 'Week 4', views: 92, inquiries: 9 }
    ]
};

export const mockClientInsightsData = {
    demographics: {
        age: [
            { name: '18-24', value: 10 },
            { name: '25-34', value: 35 },
            { name: '35-44', value: 30 },
            { name: '45-54', value: 15 },
            { name: '55+', value: 10 }
        ],
        location: [
            { name: 'North America', value: 45 },
            { name: 'Europe', value: 30 },
            { name: 'Asia', value: 15 },
            { name: 'Other', value: 10 }
        ]
    },
    frequentTopics: [
        "Career transition strategies",
        "Team management issues",
        "Work-life balance",
        "Salary negotiation"
    ],
    satisfactionScore: 4.7,
    returningClientsPercentage: 65
};

export const mockNotificationsData = [
    {
        id: 1,
        type: 'feature',
        title: 'New Video Chat Feature Available',
        description: 'You can now host video sessions directly on the platform without needing third-party tools.',
        date: '2 days ago',
        icon: 'FaLaptopCode'
    },
    {
        id: 2,
        type: 'tip',
        title: 'Increase Your Visibility',
        description: 'Complete your profile with specific expertise tags to appear in more search results.',
        date: '5 days ago',
        icon: 'FaBullhorn'
    },
    {
        id: 3,
        type: 'update',
        title: 'Platform Maintenance',
        description: 'Scheduled maintenance on June 12th from 2AM to 4AM UTC.',
        date: '1 week ago',
        icon: 'FaUsersCog'
    },
    {
        id: 4,
        type: 'feedback',
        title: 'Share Your Thoughts',
        description: 'We value your feedback! Please take our quarterly adviser satisfaction survey.',
        date: '2 weeks ago', 
        icon: 'FaRegLightbulb'
    },
    {
        id: 5,
        type: 'feature',
        title: 'Payment Analytics Dashboard',
        description: 'Track your earnings with our new analytics dashboard. View trends and payment history.',
        date: '3 weeks ago',
        icon: 'FaChartLine'
    },
    {
        id: 6,
        type: 'tip',
        title: 'Client Retention Strategies',
        description: 'Join our webinar on June 18th to learn proven strategies for improving client retention rates.',
        date: '1 month ago',
        icon: 'FaUserFriends'
    }
]; 